import { StoryblokComponent } from '@storyblok/react';
import React from 'react';

import { usePreviewMode, useStoryblok } from '@b2c-web-marketing/shared/storyblok-sdk';
import { dehydrate } from '@tanstack/react-query';
import { componentRelations } from 'apps/urban/api/relations/relations';
import { queryClient } from 'apps/urban/context/ReactQueryProvider';
import { getDynamicItemsData } from 'apps/urban/utils/getDynamicComponent/getDynamicComponent';
import { getSlugFromParams } from 'apps/urban/utils/getSlugFromParams';
import { getTemplateType } from 'apps/urban/utils/getTemplateType';
import {
  handleBlogArticleLoad,
  handleBlogCategoryLoad,
  handlePageItemLoad,
} from 'apps/urban/utils/handlePageItemLoad/handlePageItemLoad';
import { GetStaticPaths } from 'next';

import {
  getHeader,
  getFooter,
  getLocationModal,
  getCookieBanner,
  getSharedSeoSchemaItems,
  getBlogArticle,
  PageItem,
  getBlogCategory,
} from '../../../api';
import { getFooterV2 } from '../../../api/utils/getFooterV2';
import { getHeaderV2 } from '../../../api/utils/getHeaderV2';
import { getSettings } from '../../../api/utils/getSettings';
import { Layout } from '../../../components/Layout/Layout';
import { getServiceOSGlobalSettings } from '../../../utils/getServiceOSGlobalSettings';
import { getURLFromStoryblokLink } from '../../../utils/getURLFromStoryblokLink/getURLFromStoryblokLink';
import { NEXT_ISR_REVALIDATE_SECONDS } from '../../../utils/isr';

const previewFetchers = {
  blogArticle: handleBlogArticleLoad,
  blogCategory: handleBlogCategoryLoad,
  default: handlePageItemLoad,
};

export function Index({
  data,
  preview,
  header,
  footer,
  locationModal,
  cookieBanner,
  templateType,
  useV2Header,
  useV2Footer,
  keepTopMargin,
}) {
  const handleLoad =
    typeof previewFetchers[templateType] !== undefined
      ? previewFetchers[templateType]
      : previewFetchers.default;

  usePreviewMode(preview);
  const story = useStoryblok<PageItem>(data, preview, handleLoad, componentRelations);

  if (!data || !story?.content) {
    return null;
  }

  return (
    <Layout
      header={header}
      footer={footer}
      metatags={data?.content?.metatags}
      locationModal={locationModal}
      cookieBanner={cookieBanner}
      noIndex={data?.content?.noIndex}
      noFollow={data?.content?.noFollow}
      useV2Header={useV2Header}
      useV2Footer={useV2Footer}
      keepTopMargin={keepTopMargin}
    >
      <StoryblokComponent blok={story.content} />
    </Layout>
  );
}

export default Index;

export const getStaticProps = async ({ locale, params, preview = false }) => {
  locale = locale === 'default' ? 'en-gb' : locale;

  const slug = getSlugFromParams({ params });
  const templateType = await getTemplateType({ params: { slug: ['blog', slug] }, locale: locale });

  let data;

  if (templateType === 'blogArticle') {
    const res = await getBlogArticle({
      slug: `${locale}/blog/${slug}`,
      relations: componentRelations
        .split(',')
        .filter((relation) => relation !== 'blogArticle.categories')
        .join(','),
    });
    if (res?.content?.externalUrl) {
      // We don't want to render blogs that have an external link
      data = null;
    } else {
      data = res;
    }
  } else if (templateType === 'blogCategory') {
    data = await getBlogCategory({
      slug: `${locale}/blog/${slug}`,
      relations: componentRelations,
    });
  }
  if (!data) {
    return {
      notFound: true,
    };
  }

  const client = await getDynamicItemsData({ story: data, queryClient, locale });
  const dehydratedState = client ? dehydrate(client) : null;

  const settings = await getSettings({ slug: `${locale}/settings` });
  const headerV2Settings = settings?.content?.headerV2SettingsPerPage?.find(
    (setting) => getURLFromStoryblokLink(setting.link) === `${locale}/blog/${slug}`
  );

  const useV2Header = (settings?.content?.useV2Header && headerV2Settings?.useV2Header) || false;
  const useV2Footer = settings?.content?.useV2Footer || false;
  const keepTopMargin = useV2Header && headerV2Settings?.keepTopMargin;
  const keepSolidBackground = useV2Header && headerV2Settings?.keepSolidBackground;

  const header = useV2Header
    ? await getHeaderV2({ slug: `${locale}/header-v2` })
    : await getHeader({ slug: `${locale}/header` });
  const footer = useV2Footer
    ? await getFooterV2({ slug: `${locale}/footer-v2`, relations: componentRelations })
    : await getFooter({ slug: `${locale}/footer` });
  const locationModal = await getLocationModal({ slug: `${locale}/location-modal` });
  const cookieBanner = await getCookieBanner({ slug: `${locale}/cookie-consent` });
  const sharedseoschemaItems = await getSharedSeoSchemaItems({
    startsWith: `${locale}/shared-schemas`,
  });

  console.log(`getStaticProps /blog/${slug} ${locale} revalidate: ${NEXT_ISR_REVALIDATE_SECONDS}`);

  return {
    props: {
      sharedseoschemaItems: sharedseoschemaItems || null,
      cookieBanner: cookieBanner ? cookieBanner.content : null,
      footer: footer.content,
      header: { ...header.content, keepSolidBackground },
      serviceOS: getServiceOSGlobalSettings(settings),
      locationModal: locationModal.content,
      preview,
      data,
      dehydratedState,
      templateType,
      useV2Header,
      useV2Footer,
      keepTopMargin,
    },
    revalidate: NEXT_ISR_REVALIDATE_SECONDS,
  };
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => ({
  paths: [], //indicates that no page needs be created at build time
  fallback: 'blocking', //indicates the type of fallback
});
